import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import axios from "axios";
import { Button, Modal, Table } from "../../../components";
import {
  CREATE_PRICE_LIST_URL,
  DELETE_PRICE_LIST_URL,
  GET_PRICE_LISTS_URL,
  UPDATE_PRICE_LIST_URL,
} from "../../../features/UrlHelper";

const emptyForm = { kod: "", popis: "" };

const PriceListsSection = () => {
  const [priceLists, setPriceLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingPriceList, setEditingPriceList] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Chyby z formulára sa zobrazujú v modale pri formulári, chyby z načítania
  // a mazania nad tabuľkou — pri zavretom modale by inak neboli vidieť vôbec.
  const [formError, setFormError] = useState("");
  const [sectionError, setSectionError] = useState("");


  const loadPriceLists = async () => {
    try {
      const response = await axios.get(GET_PRICE_LISTS_URL);
      setPriceLists(response.data);
      setSectionError("");
    } catch (error) {
      setSectionError(error.response?.data?.message || "Cenníky sa nepodarilo načítať.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPriceLists();
  }, []);

  const openAddModal = () => {
    setEditingPriceList(null);
    setForm(emptyForm);
    setFormError("");
    setModalOpen(true);
  };

  const openEditModal = (priceList) => {
    setEditingPriceList(priceList);
    setForm({ kod: priceList.kod || "", popis: priceList.popis || "" });
    setFormError("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (!isSubmitting) setModalOpen(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setFormError("");
    const payload = { kod: form.kod, popis: form.popis };

    try {
      if (editingPriceList) {
        await axios.put(UPDATE_PRICE_LIST_URL(editingPriceList.id), payload);
      } else {
        await axios.post(CREATE_PRICE_LIST_URL, payload);
      }
      setModalOpen(false);
      await loadPriceLists();
    } catch (error) {
      setFormError(error.response?.data?.message || "Cenník sa nepodarilo uložiť.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const deletePriceList = async (id) => {
    try {
      await axios.delete(DELETE_PRICE_LIST_URL(id));
      setPriceLists((current) => current.filter((priceList) => priceList.id !== id));
      setSectionError("");
    } catch (error) {
      setSectionError(error.response?.data?.message || "Cenník sa nepodarilo zmazať.");
    } finally {
      setDeleteConfirm(null);
    }
  };

  return (
    <div className="admin__section">
      <div className="admin__sub-header">
        <h2 className="admin__section-title">Zoznam cenníkov</h2>
        <Button variant="primary" onClick={openAddModal}>
          <Plus size={16} /> Nový cenník
        </Button>
      </div>

      {sectionError && <p className="admin__form-error" role="alert">{sectionError}</p>}

      <div className="admin__table-wrapper">
        <Table
          columns={[
            { key: "kod", label: "Kód" },
            { key: "popis", label: "Popis", render: (value) => value || "-" },
            {
              key: "actions",
              label: "Akcie",
              sortable: false,
              render: (_, row) => (
                <div className="admin__table-actions">
                  <button className="admin__icon-btn" onClick={() => openEditModal(row)} title="Upraviť">
                    <Pencil size={16} />
                  </button>
                  <button
                    className="admin__icon-btn admin__icon-btn--danger"
                    onClick={() => setDeleteConfirm(row)}
                    title="Zmazať"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ),
            },
          ]}
          data={priceLists}
          loading={loading}
          searchPlaceholder="Hľadať cenník..."
          emptyMessage="Žiadne cenníky"
        />
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingPriceList ? "Upraviť cenník" : "Nový cenník"}
      >
        <form className="admin__form" onSubmit={handleSubmit}>
          <div className="admin__form-field">
            <label className="admin__form-label" htmlFor="price-list-code">Kód cenníka</label>
            <input
              id="price-list-code"
              className="admin__form-input"
              value={form.kod}
              maxLength={20}
              onChange={(event) => setForm((current) => ({
                ...current,
                kod: event.target.value.replace(/\s+/g, "").toUpperCase(),
              }))}
              placeholder="napr. ŠTANDARD"
              required
            />
          </div>
          <div className="admin__form-field">
            <label className="admin__form-label" htmlFor="price-list-description">Popis</label>
            <input
              id="price-list-description"
              className="admin__form-input"
              value={form.popis}
              maxLength={100}
              onChange={(event) => setForm((current) => ({ ...current, popis: event.target.value }))}
              placeholder="napr. Základný cenník pre koncových zákazníkov"
            />
          </div>
          {formError && <p className="admin__form-error" role="alert">{formError}</p>}
          <div className="admin__form-actions">
            <Button variant="ghost" type="button" onClick={closeModal} disabled={isSubmitting}>
              Zrušiť
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Ukladám..." : "Uložiť"}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        title="Potvrdiť zmazanie"
      >
        <p>Naozaj chcete zmazať tento cenník? Táto akcia sa nedá vrátiť.</p>
        <div className="admin__form-actions">
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>Zrušiť</Button>
          <Button
            variant="primary"
            onClick={() => deletePriceList(deleteConfirm.id)}
            style={{ backgroundColor: "var(--c-error)", borderColor: "var(--c-error)" }}
          >
            Zmazať
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default PriceListsSection;
